import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, CardActionArea, Typography, Grid, Chip, Avatar,
  Alert, Divider, CircularProgress,
} from '@mui/material';
import {
  BeachAccess, Inventory2, Event, AccountBalanceWallet, ReceiptLong,
  AssignmentReturned, CalendarMonth, Message as MessageIcon, MenuBook, School,
} from '@mui/icons-material';
import axios from 'axios';
import { useTeaching } from '../hooks/useTeaching';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const TILES = [
  { label: 'My Teaching',        desc: 'Classes, students, marks',   icon: <MenuBook />,             to: '/staff/teaching',      color: '#1b5e20', teacherOnly: true },
  { label: 'My Timetable',       desc: 'View your schedule',         icon: <CalendarMonth />,        to: '/staff/timetable',     color: '#00838f', teacherOnly: true },
  { label: 'My Leave',           desc: 'Apply & track leave',        icon: <BeachAccess />,          to: '/staff/leave',         color: '#1565c0' },
  { label: 'Store Requisitions', desc: 'Request store items',        icon: <Inventory2 />,           to: '/staff/requisitions',  color: '#2e7d32' },
  { label: 'Messages',           desc: 'School messages',            icon: <MessageIcon />,          to: '/staff/messages',      color: '#3949ab' },
  { label: 'My Activities',      desc: 'Coming soon',                icon: <Event />,                to: '/staff/activities',    color: '#6a1b9a', soon: true },
  { label: 'Imprest',            desc: 'Coming soon',                icon: <AccountBalanceWallet />, to: '/staff/imprest',       color: '#00838f', soon: true },
  { label: 'Claim Requisitions', desc: 'Coming soon',                icon: <ReceiptLong />,          to: '/staff/claims',        color: '#e65100', soon: true },
  { label: 'Imprest Surrenders', desc: 'Coming soon',                icon: <AssignmentReturned />,   to: '/staff/surrenders',    color: '#5e35b1', soon: true },
];

const balColor = (b) => b <= 0 ? 'error' : b <= 5 ? 'warning' : 'success';

export default function StaffDashboard() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { isTeaching, isAdmin, classTeacherOf, subjectClasses } = useTeaching();
  const isTeachingStaff = ['classTeacher', 'admin'].includes(user.role) || isTeaching;

  const [profile,  setProfile]  = useState(null);
  const [balances, setBalances] = useState([]);
  const [notice,   setNotice]   = useState('');
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, b] = await Promise.all([
          axios.get(`${API}/api/leave/my/profile`,  { headers: hdrs() }),
          axios.get(`${API}/api/leave/my/balances`, { headers: hdrs() }),
        ]);
        setProfile(p.data);
        setBalances(b.data || []);
      } catch (err) {
        // 400 = login not linked to an employee record; anything else is transient
        setNotice(err.response?.data?.error || 'Could not load your staff details.');
      } finally { setLoading(false); }
    })();
  }, []);

  const firstName = (user.fullName || '').split(' ')[0] || 'there';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 1.5, sm: 4 }, mb: 4, px: { xs: 1.5, sm: 2 } }}>
      {/* Greeting */}
      <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Hello, {firstName} 👋
      </Typography>
      <Typography color="text.secondary" mb={3}>Your staff self-service portal</Typography>

      {notice && <Alert severity="info" sx={{ mb: 3 }}>{notice}</Alert>}

      <Grid container spacing={2}>
        {/* Profile card */}
        <Grid item xs={12} md={5}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, fontWeight: 700 }}>
                  {(user.fullName || user.email || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography fontWeight={700}>{user.fullName || user.email}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {profile ? (profile.jobTitle || 'Staff') : (loading ? 'Loading…' : '—')}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              <InfoRow label="Employee No" value={profile?.employeeNo || user.employeeNo} />
              <InfoRow label="Department"  value={profile?.department} />
              <InfoRow label="Status"      value={profile?.status} />
            </CardContent>
          </Card>
        </Grid>

        {/* Leave balances */}
        <Grid item xs={12} md={7}>
          <Card elevation={3} sx={{ height: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <BeachAccess sx={{ color: '#1565c0' }} />
                <Typography fontWeight={700}>My Leave Balances</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {loading ? (
                <Box textAlign="center" py={2}><CircularProgress size={24} /></Box>
              ) : balances.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No leave balances yet {profile ? 'for the current year.' : ''}
                </Typography>
              ) : (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {balances.map(b => (
                    <Chip key={b.leaveTypeCode}
                      label={`${b.leaveTypeName}: ${b.balance}d`}
                      color={balColor(b.balance)} variant="outlined" />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* My Teaching at a glance — only for teaching staff */}
      {isTeachingStaff && (
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={1.5}>
              <MenuBook sx={{ color: '#1b5e20' }} />
              <Typography fontWeight={700}>My Teaching</Typography>
              {isAdmin && <Chip size="small" label="Admin — all classes" variant="outlined" sx={{ ml: 0.5 }} />}
            </Box>
            <Divider sx={{ mb: 1.5 }} />
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <TeachStat label="Classes I teach"
                  value={isAdmin ? (classTeacherOf.length || '—') : classTeacherOf.length}
                  icon={<School fontSize="small" />} color="#1565c0" onClick={() => navigate('/staff/teaching')} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TeachStat label="Subjects I teach" value={subjectClasses.length}
                  icon={<MenuBook fontSize="small" />} color="#1b5e20" onClick={() => navigate('/staff/teaching')} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TeachStat label="My Timetable" value="View"
                  icon={<CalendarMonth fontSize="small" />} color="#00838f" onClick={() => navigate('/staff/timetable')} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TeachStat label="My Teaching" value="Open"
                  icon={<MenuBook fontSize="small" />} color="#6a1b9a" onClick={() => navigate('/staff/teaching')} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <Typography variant="h6" fontWeight={700} sx={{ mt: 4, mb: 1.5 }}>Quick Actions</Typography>
      <Grid container spacing={2}>
        {TILES.filter(t => !t.teacherOnly || isTeachingStaff).map(t => (
          <Grid item xs={6} sm={4} md={3} key={t.to}>
            <Card elevation={2} sx={{ height: '100%', opacity: t.soon ? 0.72 : 1 }}>
              <CardActionArea onClick={() => navigate(t.to)} sx={{ height: '100%', p: 2 }}>
                <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1}>
                  <Avatar sx={{ bgcolor: `${t.color}1a`, color: t.color }}>{t.icon}</Avatar>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <Typography fontWeight={700} sx={{ fontSize: { xs: 14, sm: 15 } }}>{t.label}</Typography>
                      {t.soon && <Chip label="Soon" size="small" sx={{ height: 16, fontSize: 9 }} />}
                    </Box>
                    <Typography variant="caption" color="text.secondary">{t.desc}</Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box display="flex" gap={1} mb={0.6}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110, fontWeight: 500 }}>{label}</Typography>
      <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
    </Box>
  );
}

function TeachStat({ label, value, icon, color, onClick }) {
  return (
    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
      <CardActionArea onClick={onClick} sx={{ p: 1.5, height: '100%' }}>
        <Box display="flex" alignItems="center" gap={0.75} mb={0.5} sx={{ color }}>
          {icon}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>{label}</Typography>
        </Box>
        <Typography variant="h6" fontWeight={800} sx={{ color }}>{value}</Typography>
      </CardActionArea>
    </Card>
  );
}
